// src/pages/members/MemberEdit.jsx
import React, {useEffect, useState} from 'react';
import {Link, useNavigate, useParams} from 'react-router-dom';
import {get, ref} from 'firebase/database';
import {database} from '../../config/firebase';
import {useAuth} from '../../hooks/useAuth';
import {useMembers} from '../../hooks/useMembers';
import {PROFESSIONS} from "../../utils/utils";

const MemberEdit = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const {userRole} = useAuth();
    const {updateMember} = useMembers();

    const [formData, setFormData] = useState({
        prenom: '',
        nom: '',
        email: '',
        telephone: '',
        sexe: '',
        baptise: false,
        profession: '',
        churchId: ''
    });

    const [setOriginalPhone] = useState('');
    const [setOriginalEmail] = useState('');
    const [churches, setChurches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [searchProfession, setSearchProfession] = useState('');
    const [showProfessionDropdown, setShowProfessionDropdown] = useState(false);

    // Filtrer les professions en fonction de la recherche
    const filteredProfessions = PROFESSIONS.filter(profession =>
        profession.toLowerCase().includes(searchProfession.toLowerCase())
    ).concat(
        // Ajouter la profession actuelle si elle n'est pas dans la liste mais existe dans le formulaire
        formData.profession && !PROFESSIONS.includes(formData.profession) &&
        !PROFESSIONS.some(p => p.toLowerCase().includes(searchProfession.toLowerCase())) ?
            [formData.profession] : []
    );

    // Charger les données du membre et les églises
    const fetchData = async () => {
        try {
            setLoading(true);

            // Récupérer les données du membre
            const memberRef = ref(database, `users/${id}/profile`);
            const memberSnapshot = await get(memberRef);

            if (!memberSnapshot.exists()) {
                throw new Error("Ce membre n'existe pas");
            }

            const memberData = memberSnapshot.val();
            setFormData(memberData);

            // Sauvegarder les valeurs originales pour vérification
            setOriginalPhone(memberData.telephone || '');
            setOriginalEmail(memberData.email || '');

            // Vérifier les permissions
            if (userRole?.role === 'church_admin' && userRole?.churchId !== memberData.churchId) {
                throw new Error("Vous n'avez pas l'autorisation de modifier ce membre qui appartient à une autre église");
            }

            // Récupérer la liste des églises
            if (userRole?.role === 'admin') {
                // Admin peut changer l'église d'un membre
                const churchesRef = ref(database, 'churches');
                const churchesSnapshot = await get(churchesRef);

                if (churchesSnapshot.exists()) {
                    const churchesList = [];
                    churchesSnapshot.forEach((childSnapshot) => {
                        churchesList.push({
                            id: childSnapshot.key,
                            ...childSnapshot.val()
                        });
                    });

                    churchesList.sort((a, b) => a.nom.localeCompare(b.nom));
                    setChurches(churchesList);
                }
            } else if (userRole?.role === 'church_admin') {
                // Admin d'église peut seulement voir son église
                const churchRef = ref(database, `churches/${userRole.churchId}`);
                const churchSnapshot = await get(churchRef);

                if (churchSnapshot.exists()) {
                    setChurches([{
                        id: userRole.churchId,
                        ...churchSnapshot.val()
                    }]);
                }
            }
        } catch (error) {
            console.error("Erreur lors du chargement des données:", error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    fetchData();

    // Gérer le clic en dehors du dropdown des professions
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showProfessionDropdown && !event.target.closest('.profession-dropdown')) {
                setShowProfessionDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showProfessionDropdown]);

    // Gérer les changements dans le formulaire
    const handleChange = (e) => {
        const {name, value, type, checked} = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // Gérer la sélection d'une profession
    const handleProfessionSelect = (profession) => {
        setFormData(prev => ({
            ...prev,
            profession
        }));
        setSearchProfession('');
        setShowProfessionDropdown(false);
    };

    // Soumettre le formulaire
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);

        try {
            // Vérifier que tous les champs obligatoires sont remplis
            const requiredFields = ['prenom', 'nom', 'telephone', 'sexe', 'profession', 'churchId'];
            for (const field of requiredFields) {
                if (!formData[field]) {
                    throw new Error(`Le champ ${field} est obligatoire.`);
                }
            }

            // Vérifier que le téléphone a un format valide
            if (!/^\+?[0-9]{8,}$/.test(formData.telephone)) {
                throw new Error("Numéro de téléphone invalide. Veuillez utiliser un format international (ex: +22966666666).");
            }

            // Vérifier si l'email a un format valide (si fourni)
            if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
                throw new Error("Adresse email invalide.");
            }

            // Mettre à jour le membre
            const result = await updateMember(id, formData);

            if (result.success) {
                navigate(`/members/${id}`);
            } else {
                throw new Error(result.error || "Erreur lors de la mise à jour du membre");
            }
        } catch (error) {
            console.error("Erreur lors de la mise à jour du membre:", error);
            setError(error.message);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
            </div>
        );
    }

    if (error && !formData.prenom) {
        return (
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4" role="alert">
                    <p>{error}</p>
                    <div className="mt-4">
                        <Link to="/members" className="text-red-700 underline hover:text-red-600">
                            Retour à la liste des membres
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="mb-8">
                <div className="flex items-center space-x-4">
                    <Link to="/dashboard"
                          className="inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-500">
                        <svg className="-ml-1 mr-1 h-5 w-5 text-indigo-500" xmlns="http://www.w3.org/2000/svg"
                             viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd"
                                  d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                  clipRule="evenodd"/>
                        </svg>
                        Tableau de bord
                    </Link>
                    <span className="text-gray-500">/</span>
                    <Link to="/members"
                          className="inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-500">
                        Membres
                    </Link>
                    <span className="text-gray-500">/</span>
                    <Link to={`/members/${id}`}
                          className="inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-500">
                        {formData.prenom} {formData.nom}
                    </Link>
                </div>
                <h1 className="mt-4 text-2xl font-bold text-gray-900">Modifier le membre</h1>
                <p className="mt-2 text-sm text-gray-700">
                    Modifiez les informations de {formData.prenom} {formData.nom}.
                </p>
            </div>

            {error && (
                <div className="mb-6 bg-red-50 border-l-4 border-red-500 text-red-700 p-4" role="alert">
                    <p>{error}</p>
                </div>
            )}

            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                <form onSubmit={handleSubmit} className="p-6">
                    <div className="space-y-8">
                        <div>
                            <h3 className="text-lg leading-6 font-medium text-gray-900 border-b border-gray-200 pb-2">
                                Informations personnelles
                            </h3>
                            <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">

                                <div>
                                    <label htmlFor="nom" className="block text-sm font-medium text-gray-700">
                                        Nom <span className="text-red-500">*</span>
                                    </label>
                                    <div className="mt-1">
                                        <input
                                            type="text"
                                            id="nom"
                                            name="nom"
                                            value={formData.nom}
                                            onChange={handleChange}
                                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                            required
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label htmlFor="prenom" className="block text-sm font-medium text-gray-700">
                                        Prénom <span className="text-red-500">*</span>
                                    </label>
                                    <div className="mt-1">
                                        <input
                                            type="text"
                                            id="prenom"
                                            name="prenom"
                                            value={formData.prenom}
                                            onChange={handleChange}
                                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                            required
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label htmlFor="sexe" className="block text-sm font-medium text-gray-700">
                                        Sexe <span className="text-red-500">*</span>
                                    </label>
                                    <div className="mt-1">
                                        <select
                                            id="sexe"
                                            name="sexe"
                                            value={formData.sexe}
                                            onChange={handleChange}
                                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                            required
                                        >
                                            <option value="">Sélectionnez</option>
                                            <option value="M">Homme</option>
                                            <option value="F">Femme</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="flex items-center h-full pt-6">
                                    <div className="flex items-center h-5">
                                        <input
                                            id="baptise"
                                            name="baptise"
                                            type="checkbox"
                                            checked={formData.baptise}
                                            onChange={handleChange}
                                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                        />
                                    </div>
                                    <div className="ml-3 text-sm">
                                        <label htmlFor="baptise" className="font-medium text-gray-700">Cette personne
                                            est baptisée</label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div>
                            <h3 className="text-lg leading-6 font-medium text-gray-900 border-b border-gray-200 pb-2">
                                Coordonnées
                            </h3>
                            <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                                <div>
                                    <label htmlFor="telephone" className="block text-sm font-medium text-gray-700">
                                        Numéro de téléphone <span className="text-red-500">*</span>
                                    </label>
                                    <div className="mt-1">
                                        <input
                                            type="tel"
                                            id="telephone"
                                            name="telephone"
                                            value={formData.telephone}
                                            onChange={handleChange}
                                            placeholder="+22966666666"
                                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                            required
                                        />
                                    </div>
                                    <p className="mt-1 text-xs text-gray-500">Format international (ex:
                                        +22966666666)</p>
                                </div>

                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                                        Adresse e-mail (facultatif)
                                    </label>
                                    <div className="mt-1">
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div>
                            <h3 className="text-lg leading-6 font-medium text-gray-900 border-b border-gray-200 pb-2">
                                Informations supplémentaires
                            </h3>
                            <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                                <div className="relative profession-dropdown">
                                    <label htmlFor="profession" className="block text-sm font-medium text-gray-700">
                                        Profession/Occupation <span className="text-red-500">*</span>
                                    </label>
                                    <div className="mt-1">
                                        <div className="relative">
                                            <input
                                                type="text"
                                                id="profession"
                                                value={formData.profession || searchProfession}
                                                onChange={(e) => {
                                                    if (formData.profession === e.target.value) return;
                                                    setSearchProfession(e.target.value);
                                                    setFormData(prev => ({...prev, profession: ''}));
                                                    if (!showProfessionDropdown) {
                                                        setShowProfessionDropdown(true);
                                                    }
                                                }}
                                                onClick={() => setShowProfessionDropdown(true)}
                                                placeholder="Sélectionnez ou tapez pour filtrer"
                                                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                                required
                                            />
                                            <button
                                                type="button"
                                                className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                                            >
                                                <svg className="h-5 w-5 text-gray-400"
                                                     xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"
                                                     fill="currentColor">
                                                    <path fillRule="evenodd"
                                                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                                          clipRule="evenodd"/>
                                                </svg>
                                            </button>
                                        </div>
                                        {showProfessionDropdown && (
                                            <div
                                                className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                                                {filteredProfessions.length > 0 ? (
                                                    filteredProfessions.map((profession) => (
                                                        <div
                                                            key={profession}
                                                            onClick={() => handleProfessionSelect(profession)}
                                                            className="cursor-pointer hover:bg-indigo-500 hover:text-white px-4 py-2"
                                                        >
                                                            {profession}
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="text-gray-500 px-4 py-2">Aucun résultat trouvé</div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor="churchId" className="block text-sm font-medium text-gray-700">
                                        Église d'appartenance <span className="text-red-500">*</span>
                                    </label>
                                    <div className="mt-1">
                                        <select
                                            id="churchId"
                                            name="churchId"
                                            value={formData.churchId}
                                            onChange={handleChange}
                                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                            required
                                            disabled={userRole?.role !== 'admin'}
                                        >
                                            <option value="">Sélectionnez une église</option>
                                            {churches.map(church => (
                                                <option key={church.id} value={church.id}>
                                                    {church.nom}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    {userRole?.role !== 'admin' && (
                                        <p className="mt-1 text-xs text-gray-500">Seuls les administrateurs peuvent
                                            changer l'église d'un membre</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="pt-5 flex justify-end space-x-3">
                            <Link
                                to={`/members/${id}`}
                                className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                                Annuler
                            </Link>
                            <button
                                type="submit"
                                disabled={saving}
                                className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${saving ? 'opacity-70 cursor-not-allowed' : ''}`}
                            >
                                {saving ? (
                                    <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg"
                         fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                              strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Enregistrement...
                  </span>
                                ) : 'Enregistrer les modifications'}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default MemberEdit;
